import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Plug, KeyRound, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Save, Link2, ArrowLeft, Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_STYLE = {
  ok: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "Login OK" },
  login_rejected: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle, label: "Rejected" },
  mfa_required: { cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: AlertTriangle, label: "2FA enabled" },
  not_authenticated: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle, label: "Not authenticated" },
  no_credentials: { cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: AlertTriangle, label: "No credentials" },
  network_error: { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle, label: "Network error" },
};

function fmtTime(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ts; }
}

export default function AgentSetup() {
  const [loading, setLoading] = useState(true);
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({ app_url: "", email: "", password: "", gingr_location_id: "", location_name: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);
  const [mfaSession, setMfaSession] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/agent-setup/connection`);
      setConn(data);
      setLastTest(data.last_test || null);
      setForm((f) => ({
        ...f,
        app_url: data.app_url || "",
        gingr_location_id: data.gingr_location_id || "",
        location_name: data.location_name || "",
        password: "",
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Agent Setup");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.app_url || !form.email) { toast.error("Gingr URL and login email are required"); return; }
    if (!conn?.connected && !form.password) { toast.error("Password is required for first-time setup"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      await axios.post(`${API}/agent-setup/connection`, payload);
      toast.success("Gingr connection saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save connection");
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setLastTest(null);
    setMfaSession(null);
    try {
      const { data } = await axios.post(`${API}/agent-setup/test-login`, null, { timeout: 90000 });
      if (data.verdict === "mfa_pending") {
        setMfaSession(data.session_id);
        setMfaCode("");
        toast.info("Gingr emailed a 6-digit code — enter it below");
      } else {
        setLastTest(data);
        if (data.ok) toast.success("Login succeeded — refreshing lodging rooms in the background");
        else toast.error(data.message || "Login test failed");
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login test could not run");
    } finally { setTesting(false); }
  };

  const verifyMfa = async () => {
    const code = (mfaCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length < 6) { toast.error("Enter all 6 digits of the code"); return; }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/agent-setup/test-login/mfa`, { session_id: mfaSession, code }, { timeout: 90000 });
      if (data.ok) {
        setMfaSession(null); setMfaCode(""); setLastTest(data);
        toast.success("Verified — Gingr connected. Refreshing lodging rooms…");
        await load();
      } else if (data.verdict === "mfa_expired") {
        setMfaSession(null); setMfaCode(""); setLastTest(data);
        toast.error("That took too long — click Test Login again for a fresh code");
      } else {
        if (data.session_id) setMfaSession(data.session_id);
        toast.error(data.message || "That code didn't work — try the latest one");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify the code");
    } finally { setVerifying(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24" data-testid="agent-setup-loading"><Loader2 className="h-6 w-6 animate-spin text-cyan-600" /></div>;
  }

  const vstyle = lastTest ? (VERDICT_STYLE[lastTest.verdict] || VERDICT_STYLE.network_error) : null;
  const VIcon = vstyle?.Icon;
  const connected = !!conn?.connected;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="agent-setup-page">
      <Link to="/agents" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-cyan-700 transition-colors" data-testid="back-to-agents">
        <ArrowLeft className="h-4 w-4" /> All Agents
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Plug className="h-7 w-7 text-cyan-600" />
            Agent Setup
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            The Gingr automation login for <b>{conn?.name}</b>. Every agent for this location — Vaccine Compliance, Unsigned Agreements, Churn Nudge — plus the lodging &amp; belongings scrapers share it.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {connected
            ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="conn-badge"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Connected</Badge>
            : conn?.borrows
              ? <Badge className="bg-blue-50 text-blue-700 border-blue-200" data-testid="conn-badge"><Link2 className="h-3.5 w-3.5 mr-1" />Shared login</Badge>
              : <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="conn-badge">Not connected</Badge>}
        </div>
      </div>

      <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 px-4 py-3 flex items-start gap-3" data-testid="agent-setup-help">
        <Lock className="h-5 w-5 text-cyan-600 mt-0.5 shrink-0" />
        <div className="text-sm text-cyan-900">
          The Gingr URL is pre-filled from this location's POS integration. Enter the automation login email &amp; password, save, then <b>Test Login</b> to confirm it authenticates. A failure shows the exact reason here and on the <b>Command Center</b>; a success also refreshes this location's Collars / Tag-Prep rooms.
        </div>
      </div>

      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Plug className="h-5 w-5 text-cyan-600" />Gingr connection</CardTitle>
          <CardDescription>
            {connected
              ? <>Connected as <b>{conn?.email_masked}</b>. Leave the password blank to keep the current one.</>
              : conn?.borrows
                ? <>This location currently borrows a sibling login on the same Gingr app. Enter its own login below to set one directly.</>
                : <>No Gingr login connected yet for this location.</>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {lastTest && vstyle && (
            <div className={`text-sm rounded-md border px-3 py-2 flex items-start gap-2 ${vstyle.cls}`} data-testid="last-test-result">
              <VIcon className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold">{vstyle.label}.</span> {lastTest.message}
                <span className="block text-xs opacity-80 mt-0.5">Tested {fmtTime(lastTest.tested_at)}</span>
              </div>
            </div>
          )}

          {mfaSession && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3 space-y-3" data-testid="mfa-panel">
              <div className="text-sm text-amber-900 flex items-start gap-2">
                <KeyRound className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Gingr sent a 6-digit verification code to <b>{conn?.email_masked}</b>. Enter it here within a few minutes. We'll trust this connection for 60 days so the agents won't ask again.</span>
              </div>
              <div className="flex items-center gap-2">
                <Input data-testid="mfa-code-input" value={mfaCode} inputMode="numeric" maxLength={6}
                  onChange={(e) => setMfaCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") verifyMfa(); }}
                  placeholder="123456" className="w-36 text-center tracking-[0.4em] font-semibold" />
                <Button onClick={verifyMfa} disabled={verifying} data-testid="mfa-verify-btn"
                  className="bg-gradient-to-r from-amber-500 to-orange-600">
                  {verifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                  Verify code
                </Button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="app_url">Gingr URL</Label>
              <Input id="app_url" data-testid="input-app-url" placeholder="https://yourname.gingrapp.com"
                value={form.app_url} onChange={(e) => set("app_url", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="gloc">Gingr location id</Label>
              <Input id="gloc" data-testid="input-gingr-loc" placeholder="e.g. 1 (shared apps need this)"
                value={form.gingr_location_id} onChange={(e) => set("gingr_location_id", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="email">Automation login email</Label>
              <Input id="email" data-testid="input-email"
                placeholder={connected && conn?.email_masked ? `Current: ${conn.email_masked}` : "accounting@example.com"}
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="password">{connected ? "Password (leave blank to keep)" : "Password"}</Label>
              <Input id="password" type="password" data-testid="input-password"
                placeholder={connected ? "••••••••" : "Gingr password"}
                value={form.password} onChange={(e) => set("password", e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="loc_name">Gingr location name (optional)</Label>
            <Input id="loc_name" data-testid="input-loc-name" placeholder={conn?.name || "K9 Resorts location"}
              value={form.location_name} onChange={(e) => set("location_name", e.target.value)} />
          </div>

          <Separator />

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" onClick={runTest} disabled={testing || (!connected && !form.password)}
              data-testid="test-login-btn" className="border-cyan-300 text-cyan-700 hover:bg-cyan-50">
              {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Test Login
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-conn-btn"
              className="bg-gradient-to-r from-cyan-500 to-blue-600">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save connection
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
